// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartFieldPropsSchema.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { MESSAGE as ALTER_MESSAGE } from 'logic/data/Izenda.SettingsByRangeConstants';
import { MESSAGE } from './Izenda.PropertiesBuilderConstants';
import calculatedConstants from 'logic/data/Izenda.CalculatedFieldConstants';
import localizer from 'share/localization/Izenda.Localizer';

const alternativeTextMessage = ALTER_MESSAGE.M03;

export default {
  schemaKey: 'RP_FIELD_PROPERTIES',
  fields: {
    /* data source section*/
    dataSource: {
      component: 'Panel',
      props: {
        initState: true,
        collapsible: true,
        header: {
          get title() {
            return localizer.getText('COMMON_LABEL_DATA_SOURCE');
          },
          icon: 'icon-toggle-triangle'
        }
      },
      fields: {
        // type
        querySourceType: {
          get title() {
            return localizer.getText('COMMON_LABEL_TYPE');
          },
          type: 'static',
          fnValue: 'getValueByKey',
          props: {
            hiddenByState: 'hiddenByReportPartType'
          }
        },

        // name
        sourceAlias: {
          get title() {
            return localizer.getText('COMMON_LABEL_NAME');
          },
          type: 'static',
          fnValue: 'getValueByKey',
          props: {
            hiddenByState: 'hiddenByReportPartType'
          }
        },

        // field name
        fieldName: {
          get title() {
            return localizer.getText('COMMON_LABEL_FIELD_NAME');
          },
          type: 'static',
          fnValue: 'getFieldName'
        },

        // field name alias
        fieldNameAlias: {
          get title() {
            return localizer.getText('REPORT_PART_FIELD_PROP_SCHEMA_FIELD_NAME_ALIAS');
          },
          component: 'CustomInput',
          fnValue: 'getFieldNameAlias',
          props: {
            beforeOnChange: 'changeFieldNameAlias',
            validations: 'getAliasValidation',
            field: 'fieldNameAlias'
          }
        },

        // data type
        dataFieldType: {
          get title() {
            return localizer.getText('COMMON_LABEL_DATA_TYPE');
          },
          type: 'static',
          fnValue: 'getValueByKey',
          props: {
            hiddenByState: 'hiddenByReportPartType'
          }
        },

        // visible
        fieldItemVisible: {
          get title() {
            return localizer.getText('COMMON_LABEL_VISIBLE');
          },
          type: 'checkbox',
          fnValue: 'getValueByKey',
          isHidden: 'isHiddenFieldItemVisible',
          props: {
            hiddenByState: 'hiddenByReportPartType'
          }
        }
      }
    },

    dataFormattings: {
      component: 'Panel',
      props: {
        initState: true,
        collapsible: true,
        header: {
          get title() {
            return localizer.getText('REPORT_PART_FIELD_PROP_SCHEMA_DATA_FORMATTING_HEADER');
          },
          icon: 'icon-toggle-triangle'
        }
      },
      fields: {
        // function
        function: {
          get title() {
            return localizer.getText('MODEL_LABEL_FUNCTION');
          },
          type: 'select',
          fnValue: 'getValueByKey',
          isHidden: 'isHiddenFunction',
          props: {
            beforeOnChange: 'beforeChangeFunction',
            options: 'getFunctions',
            labelField: 'name',
            valueField: 'id',
            disabled: 'isRunningField'
          }
        },

        format: {
          get title() {
            return localizer.getText('REPORT_PART_FIELD_PROP_SCHEMA_FORMAT');
          },
          type: 'select',
          fnValue: 'getDataFormatType',
          props: {
            beforeOnChange: 'beforeChangeFormat',
            options: 'getDataFormats',
            labelField: 'name',
            valueField: 'customId',
            groupBy: 'category',
            showTooltip: true,
            blankOption: true,
            blankOptionText: '...'
          }
        },

        width: {
          get title() {
            return localizer.getText('REPORT_PART_PROPS_GRID_SCHEMA_LABEL_WIDTH');
          },
          component: 'SizeSetting',
          fnValue: 'getDataFormatsWidth',
          props: {
            loadValue: 'getWidthValue',
            propertyKey: 'width.value'
          }
        },

        // font family + font size
        fontFormat: {
          get title() {
            return localizer.getText('FONT_SETTINGS_LABEL_FONT');
          },
          component: 'FontFormat',
          fnValue: 'getDataFontFormat',
          props: {
            hiddenByState: 'hiddenByReportPartType'
          }
        },

        // bold, italic, underline
        fontStyle: {
          title: '',
          component: 'FontStyle',
          fnValue: 'getDataFontStyle',
          props: {
            hiddenByState: 'hiddenByReportPartType'
          }
        },

        // alignment: left, right, center,...
        alignment: {
          get title() {
            return localizer.getText('FONT_SETTINGS_LABEL_ALIGNMENT');
          },
          component: 'AlignmentSetting',
          fnValue: 'getDataAlignmentFormat',
          isHidden: 'isSeparator',
          props: {
            hiddenByState: true
          }
        },

        // VerticalAlignment: top, middle, bottom
        verticalAlignment: {
          title: '',
          component: 'VerticalAlignmentSetting',
          fnValue: 'getDataFormattingVerticalAlignmentFormat',
          isHidden: 'isSeparator',
          props: {
            hiddenByState: true
          }
        },

        // wordwrap
        columnWordWrap: {
          get title() {
            return localizer.getText('REPORT_PART_PROPS_GRID_SCHEMA_LABEL_WORD_WRAP');
          },
          component: 'ButtonHighlight',
          fnValue: 'getDataWordWrap',
          isHidden: 'isSeparator',
          staticProps: {
            defaultIcon: 'icon-word-wr'
          },
          props: {
            loadValue: 'getDataWordWrap',
            propertyKey: 'wordWrap',
            get description() {
              return localizer.getText('BUTTON_HIGHLIGHT_WRAP_TEXT');
            },
            states: [
              {
                value: false,
                icon: 'icon-word-wr'
              },
              {
                value: true,
                icon: 'icon-word-wr active'
              }
            ],
            disabledIcon: 'icon-word-wr'
          }
        },

        // sort
        sort: {
          get title() {
            return localizer.getText('REPORT_PART_FIELD_PROP_SCHEMA_SORT');
          },
          component: 'ButtonHighlight',
          fnValue: 'getValueByKey',
          isHidden: 'isHiddenSort',
          staticProps: {
            defaultIcon: 'icon-unsort'
          },
          props: {
            loadValue: 'getSortValue',
            propertyKey: 'sort',
            get description() {
              return localizer.getText('REPORT_PART_FIELD_PROP_SCHEMA_SORT');
            },
            beforeOnChange: 'beforeChangeSorting',
            loadStates: 'getSortList',
            isEmptyValue: 'isEmptySortValue',
            disabled: 'isRunningField'
          }
        },

        // color
        color: {
          get title() {
            return localizer.getText('COMMON_LABEL_COLOR');
          },
          component: 'TextCellColorSettings',
          fnValue: 'getDataTypeTextCellColor',
          staticProps: {
            allowNullValueOverwrite: true,
            cellComponent: require('modules/ReportDesigner/Fields/PropertiesBuilder/CommonSettings/Izenda.SettingsByRange')
          },
          props: {
            fnResetTextDefaultValue: 'fnResetTextDefaultValue',
            fnResetCellDefaultValue: 'fnResetCellDefaultValue',
            hiddenByState: 'hiddenColorByReportPartType',
            loadValue: 'getDataTypeTextCellColor',
            isGridReport: 'isGridReport'
          }
        },

        // alternative text: button to open pop-up
        alternativeText: {
          get title() {
            return localizer.getText('REPORT_PART_FIELD_PROP_SCHEMA_ALTERNATIVE_TEXT');
          },
          type: 'btnPopup',
          fnValue: 'getAlternativeText',
          props: {
            dataType: 'getSettingDataType',
            dateTimeFormatData: 'getDateTimeFormatData',
            fnResetDefaultValue: 'resetDefaultAlternativeText',
            hadSetting: 'hasSettingAlternativeText',
            loadValue: 'getAlternativeText',
            hiddenByState: 'hiddenAlternativeTextByReportPartType'
          },
          staticProps: {
            get title() {
              return localizer.getText('REPORT_PART_FIELD_PROP_SCHEMA_ALTERNATIVE_TEXT_SETTINGS');
            },
            className: 'icon-setting btn-icon',
            get popupTitle() {
              return localizer.getText('REPORT_PART_FIELD_PROP_SCHEMA_ALTERNATIVE_TEXT_SETTINGS');
            },
            popupContent: require('modules/ReportDesigner/Fields/PropertiesBuilder/CommonSettings/Izenda.SettingsByRange'),
            popupProps: {
              type: 'none',
              autoClose: false,
              sizeClass: 'modal-lg'
            },
            popupContentProps: {
              validateMessages: alternativeTextMessage,
              valueProps: {
                get title() {
                  return localizer.getText('REPORT_PART_FIELD_PROP_SCHEMA_ALTERNATIVE_TEXT');
                },
                type: 'text'
              },
              beforeClosing: 'beforeClosing',
              allowNullValueOverwrite: true
            }
          }
        },

        // custom URL: button to open pop-up
        customURL: {
          get title() {
            return localizer.getText('COMMON_LABEL_CUSTOM_URL');
          },
          type: 'btnPopup',
          fnValue: 'getCustomUrl',
          props: {
            checkUniqLinkOption: 'checkUniqLinkOption',
            hadSetting: 'hasSettingCustomURL',
            loadValue: 'getCustomUrl',
            hiddenIconSettingSubReport: 'hiddenIconSettingSubReport',
            fnResetDefaultValue: 'resetDefaultCustomURL',
            hiddenByState: 'hiddenByReportPartType'
          },
          isHidden: 'isHiddenCustomURL',
          staticProps: {
            get title() {
              return localizer.getText('REPORT_PART_FIELD_PROP_SCHEMA_CUSTOM_URL_SETTINGS');
            },
            className: 'icon-setting btn-icon',
            get popupTitle() {
              return localizer.getText('REPORT_PART_FIELD_PROP_SCHEMA_CUSTOM_URL_SETTINGS');
            },
            popupContent: require('modules/ReportDesigner/Fields/PropertiesBuilder/Izenda.CustomURLBody'),
            popupProps: {
              type: 'none',
              autoClose: false
            },
            popupContentProps: {
              beforeClosing: 'beforeClosing'
            }
          }
        },

        // embedded javascript: button to open pop-up
        embeddedJavascript: {
          get title() {
            return localizer.getText('COMMON_LABEL_EMBEDDED_JAVASCRIPT');
          },
          type: 'btnPopup',
          fnValue: 'getEmbeddedScript',
          props: {
            checkUniqLinkOption: 'checkUniqLinkOption',
            hadSetting: 'hasSettingEmbeddedJavascript',
            loadValue: 'getEmbeddedScript',
            hiddenIconSettingSubReport: 'hiddenIconSettingSubReport',
            fnResetDefaultValue: 'resetDefaultEmbeddedJavascript',
            hiddenByState: 'hiddenByReportPartType'
          },
          isHidden: 'isHiddenEmbeddedJavascript',
          staticProps: {
            get title() {
              return localizer.getText('REPORT_PART_FIELD_PROP_SCHEMA_EMBEDDED_JAVASCRIPT_SETTINGS');
            },
            className: 'icon-setting btn-icon',
            get popupTitle() {
              return localizer.getText('REPORT_PART_FIELD_PROP_SCHEMA_EMBEDDED_JAVASCRIPT_SETTINGS');
            },
            popupContent: require('modules/ReportDesigner/Fields/PropertiesBuilder/Izenda.EmbeddedJavascriptBody'),
            popupProps: {
              type: 'none',
              autoClose: false
            },
            popupContentProps: {
              beforeClosing: 'beforeClosing'
            }
          }
        },

        // sub-total: button to open pop-up
        subTotal: {
          get title() {
            return localizer.getText('REPORT_PART_FIELD_PROP_SCHEMA_SUBTOTAL');
          },
          component: 'CalculatedFieldProperties',
          fnValue: 'getDataSubTotal',
          props: {
            fieldStore: 'getFieldStore',
            hadSetting: 'hasSettingSubTotal',
            fieldSetting: 'getFieldSetting',
            loadValue: 'getDataSubTotal',
            fnResetDefaultValue: 'resetDefaultSubTotal',
            disabled: 'isRunningField',
            afterOnChange: 'afterOnChangeSubTotal'
          },
          isHidden: 'isHiddenSubTotal',
          staticProps: {
            requireLabel: false,
            get title() {
              return localizer.getText('REPORT_PART_FIELD_PROP_SCHEMA_SUBTOTAL_SETTINGS');
            },
            type: calculatedConstants.TYPES.SUB_TOTAL,
            className: 'icon-setting btn-icon',
            isSubTotalSetting: true
          }
        },

        // grand-total: button to open pop-up
        grandTotal: {
          get title() {
            return localizer.getText('COMMON_LABEL_GRAND_TOTAL');
          },
          component: 'CalculatedFieldProperties',
          fnValue: 'getDataGrandTotal',
          props: {
            fieldStore: 'getFieldStore',
            hadSetting: 'hasSettingGrandTotal',
            loadValue: 'getDataGrandTotal',
            fnResetDefaultValue: 'resetDefaultGrandTotal',
            disabled: 'isRunningField'
          },
          staticProps: {
            requireLabel: false,
            get title() {
              return localizer.getText('REPORT_PART_FIELD_PROP_SCHEMA_GRAND_TOTAL_SETTING');
            },
            className: 'icon-setting btn-icon',
            type: calculatedConstants.TYPES.GRAND_TOTAL
          }
        }
      }
    },

    headerFormating: {
      component: 'Panel',
      props: {
        initState: true,
        collapsible: true,
        header: {
          get title() {
            return localizer.getText('REPORT_PART_FIELD_PROP_SCHEMA_HEADER_FORMATING');
          },
          icon: 'icon-toggle-triangle'
        },
        hiddenByState: 'hiddenByReportPartType'
      },
      fields: {
        // font family + font size
        fontFormat: {
          get title() {
            return localizer.getText('FONT_SETTINGS_LABEL_FONT');
          },
          component: 'FontFormat',
          fnValue: 'getHeaderFontFormat',
          staticProps: {
            blankOption: true
          },
          props: {
            hiddenByState: 'hiddenByReportPartType'
          }
        },

        // bold, italic, underline
        fontStyle: {
          title: '',
          component: 'FontStyle',
          fnValue: 'getHeaderFontStyle',
          props: {
            hiddenByState: 'hiddenByReportPartType'
          }
        },

        // alignment: left, right, center,...
        alignment: {
          get title() {
            return localizer.getText('FONT_SETTINGS_LABEL_ALIGNMENT');
          },
          component: 'AlignmentSetting',
          fnValue: 'getHeaderAlignmentFormat',
          isHidden: 'isSeparator',
          props: {
            hiddenByState: true
          }
        },

        // VerticalAlignment: top, middle, bottom
        verticalAlignment: {
          title: '',
          component: 'VerticalAlignmentSetting',
          fnValue: 'getHeaderVerticalAlignmentFormat',
          isHidden: 'isSeparator',
          props: {
            hiddenByState: true
          }
        },

        // color
        color: {
          get title() {
            return localizer.getText('COMMON_LABEL_COLOR');
          },
          component: 'ColorStyling',
          fnValue: 'getHeaderColorStyling',
          props: {
            type: 'photoshop',
            position: 'left'
          }
        },

        // wordwrap
        wordWrap: {
          get title() {
            return localizer.getText('REPORT_PART_FIELD_PROP_SCHEMA_WORD_WRAP');
          },
          component: 'ButtonHighlight',
          fnValue: 'getValueByKey',
          staticProps: {
            defaultIcon: 'icon-word-wr'
          },
          props: {
            loadValue: 'getWordWrapValue',
            propertyKey: 'wordWrap',
            get description() {
              return localizer.getText('BUTTON_HIGHLIGHT_WRAP_TEXT');
            },
            states: [
              {
                value: false,
                icon: 'icon-word-wr'
              },
              {
                value: true,
                icon: 'icon-word-wr active'
              }
            ],
            hiddenByState: 'hiddenByReportPartType'
          }
        },

        // verticalHeader
        verticalHeader: {
          get title() {
            return localizer.getText('REPORT_PART_FIELD_PROP_SCHEMA_VERTICAL_HEADER');
          },
          type: 'checkbox',
          fnValue: 'getValueByKey',
          isHidden: 'isVerticalHeaderHidden',
          props: {
          }
        },

        // verticalHeaderHeight
        verticalHeaderHeight: {
          get title() {
            return localizer.getText('REPORT_PART_FIELD_PROP_SCHEMA_HEADER_HEIGHT');
          },
          component: 'SizeSetting',
          fnValue: 'getVerticalHeaderHeight',
          isHidden: 'isVerticalHeaderHeightHidden',
          props: {
            loadValue: 'getHeightValue',
            propertyKey: 'verticalHeaderHeight.value'
          }
        },

        // column group
        columnGroup: {
          get title() {
            return localizer.getText('REPORT_PART_FIELD_PROP_SCHEMA_COLUMN_GROUP');
          },
          component: 'CustomInput',
          fnValue: 'getValueByKey',
          props: {
            title: MESSAGE.COLUMN_GROUP_TOOLTIP,
            hiddenByState: 'hiddenByReportPartType'
          }
        }
      }
    },

    /* drill down */
    drillDown: {
      component: 'Panel',
      props: {
        initState: true,
        collapsible: true,
        header: {
          get title() {
            return localizer.getText('REPORT_PART_FIELD_PROP_SCHEMA_DRILL_DOWN');
          },
          icon: 'icon-toggle-triangle'
        },
        hiddenByState: 'hiddenByReportPartType'
      },
      fields: {
        // width
        subReport: {
          get title() {
            return localizer.getText('REPORT_PART_FIELD_PROP_SCHEMA_SUB_REPORT');
          },
          type: 'btnPopup',
          fnValue: 'getSubReport',
          isHidden: 'isHiddenSubReport',
          props: {
            fieldStore: 'getFieldStore',
            className: 'icon-setting btn-icon',
            stypeOptions: 'stypeOptions',
            hiddenIconSettingSubReport: 'hiddenIconSettingSubReport',
            checkUniqLinkOption: 'checkUniqLinkOption',
            checkEmbeddedLinkOption: 'checkEmbeddedLinkOption',
            hadSetting: 'hasSettingSubReport',
            loadValue: 'getSubReport',
            fnResetDefaultValue: 'resetDefaultSubReport'
          },
          staticProps: {
            get title() {
              return localizer.getText('REPORT_PART_FIELD_PROP_SCHEMA_SUB_REPORT_SETTING');
            },
            get popupTitle() {
              return localizer.getText('REPORT_PART_FIELD_PROP_SCHEMA_SUB_REPORT_SETTING');
            },
            popupContent: require('modules/ReportDesigner/Fields/PropertiesBuilder/Izenda.SubReportBody'),
            popupProps: {
              type: 'none',
              autoClose: false
            },
            popupContentProps: {
              settingsBtn: [
                {
                  get label() {
                    return localizer.getText('REPORT_PART_FIELD_PROP_SCHEMA_ADD_FIELD_MAPPING');
                  },
                  action: 'addMappingField',
                  ref: 'addMappingField',
                  disabledBy: 'selectedReport'
                }
              ],
              beforeClosing: 'beforeClosing'
            }
          }
        }
      }
    }
  }
};



// WEBPACK FOOTER //
// ./ui/logic/data/Izenda.ReportPartFieldPropsSchema.js